/* @flow */
import _ from 'lodash';
import ChatActions from 'chat/actions/ChatActions';
import ChatMember from 'entities/ChatMember';
import ChatMembers from 'collections/ChatMembers';
import CurrentUser from 'auth/CurrentUser';
import Group from 'entities/Group';
import GroupStore from 'groups/stores/GroupStore';
import PotentialChatMember from 'entities/PotentialChatMember';
import Relationship from 'entities/Relationship';
import { rootGroup } from 'entities/RootGroup';
import Subscriber from 'entities/Subscriber';
import User from 'entities/User';
import { promiseRelationship } from 'chat/utils/ChatPromises';

// basically an entity that has .id and .getName()\
// NOTE: The id MUST be the id of a User!
export type NamedInterlocutor = PotentialChatMember | Subscriber | User | ChatMember;

const groupForSubscription = subscription => GroupStore.getGroup(subscription.getGroupId());

function getGroupForRelationship(relationship: Relationship): Group {
  const myGroups = GroupStore.getGroups().toArray();
  const toMe = relationship.getSubscriptionsToMe().map(groupForSubscription);
  const toUser = relationship.getSubscriptionsToUser().map(groupForSubscription);
  const otherGroups = _.union(toMe, toUser);
  const sharedGroups = _.intersection(myGroups, otherGroups);
  return sharedGroups.length > 0 ? sharedGroups[0] : rootGroup;
}

export function getGroupForMember(otherMember: NamedInterlocutor, group: ?Group): Group {
  const canJoinGivenGroup =
    group &&
    ((otherMember instanceof PotentialChatMember && otherMember.isMemberOfGroup(group.id)) ||
      otherMember instanceof Subscriber);

  if (canJoinGivenGroup && group && group.getCode()) {
    return group;
  } else if (otherMember instanceof PotentialChatMember) {
    const myGroups = GroupStore.getGroups();
    // $FlowIssue
    const sharedGroups = myGroups.filter(g => otherMember.isMemberOfGroup(g.id));
    return sharedGroups.length > 0 ? sharedGroups[0] : rootGroup;
  }
  return rootGroup;
}

// TODO maybe make this a promise for the pathname and let the component do
// the router pusher so we're not passing the router around
export function pushRouterToChatWithMember(
  router: { push: Function },
  showRootContext: Function,
  hideRootContext: Function,
  otherMember: NamedInterlocutor,
  group: ?Group,
  catchHandler?: Function
): Promise<void> {
  const commonGroup = getGroupForMember(otherMember, group);

  if (commonGroup === rootGroup) {
    return promiseRelationship(otherMember)
      .then(relationship => {
        // TODO fall back to schools, organizations
        const relatedGroup = getGroupForRelationship(relationship);

        if (relatedGroup === rootGroup) {
          getChatAndShowRootContext(showRootContext, relationship);
        } else {
          routeWithSharedGroup(router, relatedGroup, otherMember, hideRootContext, relationship);
        }
      })
      .catch(() => {
        const currentMember: ChatMember = ChatMembers.buildChatMember(CurrentUser.getUser());
        // $FlowFixMe
        ChatActions.loadLocal([currentMember, otherMember], otherMember.getName());
        ChatActions.resetSelection(); // deselect current chat to focus on local
        showRootContext();
        if (catchHandler) {
          catchHandler();
        }
      });
  } else {
    return promiseRelationship(otherMember)
      .then(relationship => {
        routeWithSharedGroup(router, commonGroup, otherMember, hideRootContext, relationship);
      })
      .catch(() => {
        const pathname = `/classes/${commonGroup.getCodeOrError()}/messages/new/${otherMember.id}`;
        router.push({ pathname });
        hideRootContext();
        if (catchHandler) {
          catchHandler();
        }
      });
  }
}

function getChatAndShowRootContext(showRootContext: Function, relationship: Relationship) {
  const chat = relationship.getChat();
  if (chat) {
    ChatActions.selectChat(chat.id);
  } else {
    const currentMember: ChatMember = ChatMembers.buildChatMember(CurrentUser.getUser());
    const relatedMember = ChatMembers.buildChatMember(relationship.getRelatedUser());
    ChatActions.loadLocal([currentMember, relatedMember], relatedMember.getName());
    ChatActions.resetSelection(); // deselect current chat to focus on local
  }
  showRootContext();
}

function routeWithSharedGroup(
  router: { push: Function },
  commonGroup: Group,
  otherMember: NamedInterlocutor,
  hideRootContext: Function,
  relationship: Relationship
) {
  const chat = relationship.getChat();
  const pathname = chat
    ? `/classes/${commonGroup.getCodeOrError()}/messages/${chat.id}`
    : `/classes/${commonGroup.getCodeOrError()}/messages/new/${relationship.id}`;
  router.push({ pathname });
  hideRootContext();
}
